import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, FormControl, FormLabel, Input, Select, Textarea,
  Table, Thead, Tbody, Tr, Th, Td, Text, Badge, HStack, useToast, VStack,
  IconButton, Divider, SimpleGrid,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { MdAdd, MdExitToApp } from 'react-icons/md';
import type { Detencion, PertenenciaCustodia } from './juzgadoTypes';
import juzgadoApi from '../../services/juzgadoApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  detencion: Detencion | null;
}

const EMPTY: Partial<PertenenciaCustodia> = {
  nombre: '',
  estadoObjeto: 'Usado',
  cantidad: 1,
  fechaIngreso: new Date().toISOString().substring(0, 10),
};

export default function ModalPertenencias({ isOpen, onClose, detencion }: Props) {
  const [pertenencias, setPertenencias] = useState<PertenenciaCustodia[]>([]);
  const [form, setForm] = useState<Partial<PertenenciaCustodia>>(EMPTY);
  const [agregar, setAgregar] = useState(false);
  const [salidaId, setSalidaId] = useState<number | null>(null);
  const [salidaForm, setSalidaForm] = useState({ fechaSalida: new Date().toISOString().substring(0, 10), responsableEntrega: '', responsableRecibeS: '' });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const set = (k: keyof PertenenciaCustodia, v: any) => setForm(p => ({ ...p, [k]: v }));

  const cargar = async () => {
    if (!detencion?.id) return;
    const data = await juzgadoApi.listarPertenencias(detencion.id);
    setPertenencias(data);
  };

  useEffect(() => {
    if (isOpen) cargar();
  }, [isOpen, detencion]);

  const handleAgregar = async () => {
    if (!detencion || !form.nombre) return toast({ title: 'Nombre del objeto requerido', status: 'warning' });
    setSaving(true);
    try {
      await juzgadoApi.crearPertenencia(detencion.id, form);
      toast({ title: 'Pertenencia registrada', status: 'success' });
      setAgregar(false);
      setForm({ ...EMPTY });
      await cargar();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSalida = async () => {
    if (!salidaId) return;
    setSaving(true);
    try {
      await juzgadoApi.registrarSalidaPertenencia(salidaId, salidaForm);
      toast({ title: 'Salida registrada', status: 'success' });
      setSalidaId(null);
      await cargar();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const diasEnCustodia = (fechaIngreso: string) => {
    const diff = Date.now() - new Date(fechaIngreso).getTime();
    return Math.floor(diff / 86400000);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
          Pertenencias en Custodia — {detencion?.folio}
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody>
          <HStack justify="flex-end" mb={3}>
            {detencion && pertenencias.length > 0 && (
              <Button size="sm" leftIcon={<MdExitToApp />} colorScheme="red" variant="outline" as="a"
                href={juzgadoApi.pdfInventarioPertenencias(detencion.id)} target="_blank">
                Inventario PDF
              </Button>
            )}
            <Button size="sm" leftIcon={<MdAdd />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={() => setAgregar(!agregar)}>
              {agregar ? 'Cancelar' : 'Agregar Objeto'}
            </Button>
          </HStack>

          {agregar && (
            <VStack p={4} border="1px solid" borderColor="#bb8c52" borderRadius="md" mb={4} spacing={3} align="stretch">
              <Text fontWeight="semibold" color="#5b182b">Nuevo objeto</Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Nombre del objeto</FormLabel>
                  <Input value={form.nombre ?? ''} onChange={e => set('nombre', e.target.value)} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Estado</FormLabel>
                  <Select value={form.estadoObjeto ?? ''} onChange={e => set('estadoObjeto', e.target.value)} borderColor="#bb8c52">
                    <option value="Nuevo">Nuevo</option>
                    <option value="Dañado">Dañado</option>
                    <option value="Usado">Usado</option>
                    <option value="Otro">Otro</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Cantidad</FormLabel>
                  <Input type="number" value={form.cantidad ?? 1} onChange={e => set('cantidad', Number(e.target.value))} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Fecha de ingreso</FormLabel>
                  <Input type="date" value={form.fechaIngreso ?? ''} onChange={e => set('fechaIngreso', e.target.value)} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Responsable que recibe</FormLabel>
                  <Input value={form.responsableRecibe ?? ''} onChange={e => set('responsableRecibe', e.target.value)} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
                </FormControl>
              </SimpleGrid>
              <FormControl>
                <FormLabel fontSize="sm">Descripción</FormLabel>
                <Textarea value={form.descripcion ?? ''} onChange={e => set('descripcion', e.target.value)} rows={2} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} />
              </FormControl>
              <HStack justify="flex-end">
                <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} size="sm" isLoading={saving} onClick={handleAgregar}>Guardar Objeto</Button>
              </HStack>
            </VStack>
          )}

          {salidaId && (
            <VStack p={4} border="1px solid" borderColor="orange.300" borderRadius="md" mb={4} spacing={2} align="stretch">
              <Text fontWeight="semibold" color="orange.600">Registrar salida de objeto #{salidaId}</Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
                <FormControl>
                  <FormLabel fontSize="sm">Fecha de salida</FormLabel>
                  <Input type="date" value={salidaForm.fechaSalida} onChange={e => setSalidaForm(p => ({ ...p, fechaSalida: e.target.value }))} borderColor="#bb8c52" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Responsable que entrega</FormLabel>
                  <Input value={salidaForm.responsableEntrega} onChange={e => setSalidaForm(p => ({ ...p, responsableEntrega: e.target.value }))} borderColor="#bb8c52" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Recibe (ciudadano)</FormLabel>
                  <Input value={salidaForm.responsableRecibeS} onChange={e => setSalidaForm(p => ({ ...p, responsableRecibeS: e.target.value }))} borderColor="#bb8c52" />
                </FormControl>
              </SimpleGrid>
              <HStack>
                <Button size="sm" colorScheme="orange" isLoading={saving} onClick={handleSalida}>Confirmar Salida</Button>
                <Button size="sm" variant="ghost" onClick={() => setSalidaId(null)}>Cancelar</Button>
              </HStack>
            </VStack>
          )}

          <Table size="sm">
            <Thead bg="gray.100">
              <Tr>
                <Th>Código</Th>
                <Th>Objeto</Th>
                <Th>Estado</Th>
                <Th>Cantidad</Th>
                <Th>Días custodia</Th>
                <Th>Salida</Th>
                <Th>Acción</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pertenencias.length === 0 && (
                <Tr><Td colSpan={7} textAlign="center" color="gray.400">Sin pertenencias registradas</Td></Tr>
              )}
              {pertenencias.map(p => (
                <Tr key={p.id}>
                  <Td fontWeight="medium" color="#5b182b">{p.codigoUnico}</Td>
                  <Td>{p.nombre}</Td>
                  <Td><Badge>{p.estadoObjeto}</Badge></Td>
                  <Td>{p.cantidad}</Td>
                  <Td>{p.fechaSalida ? '—' : diasEnCustodia(p.fechaIngreso) + ' días'}</Td>
                  <Td>{p.fechaSalida ? new Date(p.fechaSalida).toLocaleDateString('es-MX') : <Badge colorScheme="orange">En custodia</Badge>}</Td>
                  <Td>
                    {!p.fechaSalida && (
                      <IconButton aria-label="Salida" icon={<MdExitToApp />} size="xs" colorScheme="orange" onClick={() => setSalidaId(p.id)} />
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
